import React from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Search, ArrowRight } from 'lucide-react';
import { selectAllProducts } from '../../store/slices/productsSlice';

const SearchDropdown = ({ query, onSelect, onViewAll }) => {
  const allProducts = useSelector(selectAllProducts);
  
  const filteredProducts = allProducts
    .filter(product => {
      const status = String(product?.status || "").trim().toLowerCase();
      if (status === "private") return false;
      return (
        product.name.toLowerCase().includes(query.toLowerCase()) ||
      product.description.toLowerCase().includes(query.toLowerCase()) ||
      product.category.toLowerCase().includes(query.toLowerCase())
      );
    })
    .slice(0, 5);

  const highlightText = (text, highlight) => {
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span key={index} className="bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  if (filteredProducts.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="absolute left-0 right-0 top-full z-50 mt-2 rounded-3xl border border-gray-100 bg-white/95 py-4 shadow-xl backdrop-blur-md dark:border-gray-800 dark:bg-gray-900/95"
      >
        <div className="flex items-center justify-center px-4 py-8 text-sm font-bold text-gray-500 dark:text-gray-400">
          <span className="mr-2 flex h-9 w-9 items-center justify-center rounded-2xl bg-gray-100 dark:bg-gray-950">
            <Search className="h-4 w-4" />
          </span>
          <span>No products found for "{query}"</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="absolute left-0 right-0 top-full z-50 mt-2 max-h-96 overflow-y-auto rounded-3xl border border-gray-100 bg-white/95 py-2 shadow-xl backdrop-blur-md dark:border-gray-800 dark:bg-gray-900/95"
    >
      {filteredProducts.map((product) => (
        <button
          key={product.id}
          onClick={() => onSelect(product)}
          className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <img
            src={product.image || '/icon.png'}
            alt={product.name}
            className="h-11 w-11 rounded-2xl object-cover"
          />
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-bold text-gray-950 dark:text-white">
              {highlightText(product.name, query)}
            </p>
            <p className="mt-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              {product.price} • {product.category}
            </p>
          </div>
        </button>
      ))}
      
      {filteredProducts.length > 0 && (
        <button
          onClick={onViewAll}
          className="flex w-full items-center justify-center gap-2 border-t border-gray-100 px-4 py-3 text-sm font-black text-primary-600 transition-colors duration-200 hover:bg-primary-50 dark:border-gray-800 dark:text-primary-300 dark:hover:bg-primary-900/20"
        >
          <span>View all results for "{query}"</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </motion.div>
  );
};

export default SearchDropdown;
